import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { MessagesSquare, Send, Users, Flame, History, ImagePlus, X, Loader2, Hash, BookOpen, ArrowUpRight, Bell, Check, Bookmark } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES } from "../lib/categories";
import BadgeChip, { useBadges } from "../components/BadgeChip";
import Reveal from "../components/Reveal";

const PAGE_SIZE = 30;

const topicLabel = (slug) => CATEGORIES.find((c) => c.slug === slug)?.label || slug;

const topicPillCls = (active) =>
  `whitespace-nowrap text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-colors duration-300 ${active ? "bg-kinetic text-obsidian border-kinetic font-medium" : "border-white/10 text-ash hover:text-porcelain hover:border-white/25"}`;

function MiniAvatar({ url, name, className = "w-6 h-6 text-[9px]" }) {
  return url ? (
    <img src={url} alt={name} className={`${className} rounded-full object-cover border border-obsidian`} />
  ) : (
    <span className={`${className} rounded-full bg-white/10 border border-obsidian flex items-center justify-center font-display font-bold text-porcelain`}>
      {(name || "?").charAt(0).toUpperCase()}
    </span>
  );
}

export default function LoungePage() {
  const { user, openAuth, setUser } = useAuth();
  const badges = useBadges();
  const [messages, setMessages] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [presence, setPresence] = useState([]);
  const [followed, setFollowed] = useState([]);

  useEffect(() => {
    if (user) setFollowed(user.followed_topics || []);
  }, [user]);

  const toggleFollow = async (slug) => {
    const prev = followed;
    const next = prev.includes(slug) ? prev.filter((t) => t !== slug) : [...prev, slug];
    setFollowed(next);
    try {
      const r = await api.put("/users/me/topics", { topics: next });
      setFollowed(r.data.followed_topics);
      setUser((u) => (u ? { ...u, followed_topics: r.data.followed_topics } : u));
    } catch (err) {
      setFollowed(prev);
      toast.error(formatApiError(err));
    }
  };
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [topicFilter, setTopicFilter] = useState("");
  const [draftTopic, setDraftTopic] = useState("");
  const [topicPickerOpen, setTopicPickerOpen] = useState(false);
  const fileInput = useRef(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const lastTs = useRef(null);
  const earliestTs = useRef(null);
  const bottomRef = useRef(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    const onScroll = () => {
      stickToBottom.current = window.innerHeight + window.scrollY > document.documentElement.scrollHeight - 260;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    lastTs.current = null;
    earliestTs.current = null;
    setMessages(null);
    setHasMore(false);
    let alive = true;
    const load = async () => {
      try {
        const firstLoad = !lastTs.current;
        const params = lastTs.current ? { after: lastTs.current } : {};
        if (topicFilter) params.topic = topicFilter;
        const r = await api.get("/lounge/messages", { params });
        if (!alive) return;
        const incoming = r.data;
        if (incoming.length) {
          lastTs.current = incoming[incoming.length - 1].created_at;
          if (firstLoad) {
            earliestTs.current = incoming[0].created_at;
            setHasMore(incoming.length >= 50);
          }
          setMessages((prev) => {
            const seen = new Set((prev || []).map((m) => m.id));
            return [...(prev || []), ...incoming.filter((m) => !seen.has(m.id))].slice(-500);
          });
        } else {
          if (firstLoad) setHasMore(false);
          setMessages((prev) => prev ?? []);
        }
      } catch (e) { /* keep polling */ }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [topicFilter]);

  useEffect(() => {
    let alive = true;
    const loadStats = () => {
      api.get("/community/stats").then((r) => { if (alive) setStats(r.data); }).catch(() => {});
      api.get("/lounge/leaderboard").then((r) => { if (alive) setLeaderboard(r.data); }).catch(() => {});
    };
    loadStats();
    const t = setInterval(loadStats, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const ping = () => api.post("/lounge/presence").then((r) => { if (alive) setPresence(r.data); }).catch(() => {});
    ping();
    const t = setInterval(ping, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    if (user !== false) return;
    api.get("/lounge/presence").then((r) => setPresence(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (stickToBottom.current) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadEarlier = async () => {
    if (!earliestTs.current || loadingEarlier) return;
    setLoadingEarlier(true);
    const prevHeight = document.documentElement.scrollHeight;
    const prevY = window.scrollY;
    try {
      const params = { before: earliestTs.current, limit: PAGE_SIZE };
      if (topicFilter) params.topic = topicFilter;
      const r = await api.get("/lounge/messages", { params });
      const older = r.data;
      if (older.length) {
        earliestTs.current = older[0].created_at;
        setMessages((prev) => {
          const seen = new Set((prev || []).map((m) => m.id));
          return [...older.filter((m) => !seen.has(m.id)), ...(prev || [])];
        });
        requestAnimationFrame(() => {
          window.scrollTo(0, prevY + (document.documentElement.scrollHeight - prevHeight));
        });
      }
      setHasMore(older.length === PAGE_SIZE);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingEarlier(false);
    }
  };

  const toggleFlame = async (m) => {
    if (!user) {
      openAuth("login");
      return;
    }
    setMessages((prev) => (prev || []).map((x) =>
      x.id === m.id ? { ...x, flamed: !x.flamed, flames: Math.max(0, (x.flames || 0) + (x.flamed ? -1 : 1)) } : x
    ));
    try {
      const r = await api.post(`/lounge/messages/${m.id}/flame`);
      setMessages((prev) => (prev || []).map((x) => x.id === m.id ? { ...x, flamed: r.data.flamed, flames: r.data.flames } : x));
    } catch (err) {
      setMessages((prev) => (prev || []).map((x) => x.id === m.id ? { ...x, flamed: m.flamed, flames: m.flames } : x));
      toast.error(formatApiError(err));
    }
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd);
      setImage({ url: up.data.url });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const toggleSave = async (item) => {
    if (!user) {
      openAuth("login");
      return;
    }
    try {
      const r = await api.post("/users/me/saves", item);
      setUser((u) => (u ? { ...u, saves: r.data.saves } : u));
      toast.success(r.data.saved ? "Saved to your scrapbook." : "Removed from your scrapbook.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isSaved = (key) => (user?.saves || []).some((s) => s.key === key);

  const send = async () => {
    const body = draft.trim();
    if ((!body && !image) || sending || uploading) return;
    setSending(true);
    try {
      const r = await api.post("/lounge/messages", { body, image_url: image?.url || "", topic: draftTopic });
      lastTs.current = r.data.created_at;
      stickToBottom.current = true;
      setMessages((prev) => [...(prev || []), r.data].slice(-500));
      setDraft("");
      setImage(null);
      setDraftTopic("");
      setTopicPickerOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="lounge-page" className="max-w-[900px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-4">Community · Live</p>
        <h1 className="font-display font-black uppercase text-4xl sm:text-6xl tracking-tight break-words">
          The <span className="font-serif italic normal-case text-ash">Lounge</span>
        </h1>
        <p className="text-ash mt-5 max-w-xl leading-relaxed">
          One room, every reader. No threads, no topics — just the men of the magazine talking it out in real time.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="flex flex-wrap items-center gap-3 mt-8">
          <span data-testid="lounge-stats-members" className="flex items-center gap-2 border border-white/10 rounded-full px-4 py-2 text-[11px] font-mono uppercase tracking-[0.18em] text-porcelain">
            <Users size={13} className="text-kinetic" />
            {stats ? `${stats.members} member${stats.members === 1 ? "" : "s"} and counting` : "Counting members…"}
          </span>
          <span data-testid="lounge-stats-active" className="flex items-center gap-2.5 border border-white/10 rounded-full px-4 py-2 text-[11px] font-mono uppercase tracking-[0.18em] text-ash">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kinetic opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-kinetic"></span>
            </span>
            {presence.length > 0 ? `${presence.length} in the room now` : "the room is quiet"}
            {presence.length > 0 && (
              <span data-testid="lounge-presence" className="flex -space-x-1.5">
                {presence.slice(0, 5).map((p) => (
                  <MiniAvatar key={p.user_id} url={p.avatar_url} name={p.name} />
                ))}
              </span>
            )}
          </span>
        </div>
      </Reveal>

      {leaderboard.length > 0 && (
        <Reveal delay={0.1}>
          <div data-testid="lounge-leaderboard" className="mt-6 border border-white/10 bg-white/[0.02] p-5">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-ash mb-4 flex items-center gap-2">
              <Flame size={12} className="text-kinetic" /> This week's most flamed
            </p>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {leaderboard.map((r, i) => (
                <Link to={`/member/${r.author_id}`} key={r.author_id} data-testid={`lounge-leaderboard-entry-${i}`}
                  className="flex items-center gap-3 shrink-0 border border-white/10 hover:border-kinetic/50 rounded-full pl-1.5 pr-5 py-1.5 transition-colors duration-300">
                  <span className={`pl-2 text-[10px] font-mono ${i === 0 ? "text-kinetic" : "text-ash/70"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <MiniAvatar url={r.author_avatar} name={r.author_name} className="w-7 h-7 text-[10px]" />
                  <span className="text-xs font-medium text-porcelain">{r.author_name}</span>
                  <span className="flex items-center gap-1 text-[10px] font-mono text-kinetic">
                    <Flame size={10} fill="currentColor" /> {r.flames}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {user && (
        <Reveal delay={0.11}>
          <div data-testid="lounge-follow-topics" className="mt-4 border border-dashed border-white/15 p-5">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-ash mb-1.5 flex items-center gap-2">
              <Bell size={12} className="text-cobalt" /> Dispatch topics
            </p>
            <p className="text-xs text-ash/80 mb-4">Follow a room and its best moments land in your Friday Dispatch email.</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {CATEGORIES.map((c) => (
                <button key={c.slug} data-testid={`lounge-follow-${c.slug}`} onClick={() => toggleFollow(c.slug)}
                  className={`${topicPillCls(followed.includes(c.slug))} inline-flex items-center gap-1.5`}>
                  {followed.includes(c.slug) && <Check size={11} />}{c.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.12}>
        <div data-testid="lounge-topic-filter" className="mt-10 flex gap-2 overflow-x-auto no-scrollbar pb-1">
          <button data-testid="lounge-topic-filter-all" onClick={() => setTopicFilter("")}
            className={topicPillCls(topicFilter === "")}>
            All topics
          </button>
          {CATEGORIES.map((c) => (
            <button key={c.slug} data-testid={`lounge-topic-filter-${c.slug}`}
              onClick={() => setTopicFilter(topicFilter === c.slug ? "" : c.slug)}
              className={topicPillCls(topicFilter === c.slug)}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="mt-4 border-t border-white/10">
          {!messages ? (
            <p className="font-mono text-ash text-xs uppercase tracking-[0.3em] py-24 text-center animate-pulse">Warming up the room…</p>
          ) : messages.length === 0 ? (
            <div data-testid="lounge-empty-state" className="border border-dashed border-white/15 border-t-0 py-24 text-center">
              <MessagesSquare size={26} className="text-ash mx-auto mb-4" />
              <p className="font-mono text-ash text-xs uppercase tracking-[0.3em]">The room is quiet — say the first thing.</p>
            </div>
          ) : (
            <>
              {hasMore && (
                <div className="py-5 border-b border-white/10 text-center">
                  <button
                    data-testid="lounge-load-earlier-button"
                    onClick={loadEarlier}
                    disabled={loadingEarlier}
                    className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-ash hover:text-kinetic border border-white/10 hover:border-kinetic/50 rounded-full px-5 py-2.5 transition-colors duration-300 disabled:opacity-40">
                    <History size={13} /> {loadingEarlier ? "Loading…" : "Load earlier conversation"}
                  </button>
                </div>
              )}
              <div data-testid="lounge-message-list">
                {messages.map((m) => {
                  const own = user && user.id === m.author_id;
                  return (
                    <div key={m.id} data-testid="lounge-message-row" className={`flex gap-3.5 py-4 border-b border-white/5 ${own ? "border-l-2 border-l-kinetic pl-4 -ml-4" : ""}`}>
                      {m.author_avatar ? (
                        <img src={m.author_avatar} alt={m.author_name} className="shrink-0 w-9 h-9 rounded-full object-cover border border-white/10" />
                      ) : (
                        <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-display font-extrabold text-xs ${own ? "bg-kinetic text-obsidian" : "bg-white/10 text-porcelain"}`}>
                          {(m.author_name || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2.5 flex-wrap">
                          <Link to={`/member/${m.author_id}`} data-testid="lounge-message-author"
                            className={`text-sm font-medium hover:text-kinetic transition-colors ${own ? "text-kinetic" : "text-porcelain"}`}>
                            {m.author_name}
                          </Link>
                          <BadgeChip badge={(badges[m.author_id] || [])[0]} />
                          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-ash/70">
                            {format(new Date(m.created_at), "HH:mm")}
                          </span>
                          {m.topic && (
                            <button data-testid={`lounge-message-topic-${m.id}`} onClick={() => setTopicFilter(m.topic)}
                              title={`Filter by ${topicLabel(m.topic)}`}
                              className="text-[10px] font-mono uppercase tracking-[0.15em] text-cobalt hover:text-kinetic transition-colors">
                              #{topicLabel(m.topic)}
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-porcelain/90 leading-relaxed mt-1 break-words whitespace-pre-wrap">{m.body}</p>
                        {m.image_url && (
                          <div className="relative inline-block mt-2">
                            <a href={m.image_url} target="_blank" rel="noopener noreferrer" data-testid={`lounge-message-image-${m.id}`}>
                              <img src={m.image_url} alt="Shared in the lounge" loading="lazy"
                                className="max-w-[260px] sm:max-w-sm max-h-64 rounded-xl border border-white/10 object-cover hover:border-kinetic/50 transition-colors duration-300" />
                            </a>
                            <button
                              data-testid={`lounge-photo-save-${m.id}`}
                              onClick={() => toggleSave({ type: "photo", ref_id: m.id, image_url: m.image_url, title: `Shared by ${m.author_name}`, url: "/lounge" })}
                              title={user ? (isSaved(`photo:${m.id}`) ? "Remove from scrapbook" : "Save to scrapbook") : "Sign in to save"}
                              className={`absolute top-2 right-2 p-2 rounded-full bg-obsidian/70 backdrop-blur border border-white/15 transition-colors duration-300 ${isSaved(`photo:${m.id}`) ? "text-kinetic border-kinetic/50" : "text-ash hover:text-kinetic"}`}>
                              <Bookmark size={13} fill={isSaved(`photo:${m.id}`) ? "currentColor" : "none"} />
                            </button>
                          </div>
                        )}
                        {m.article_slug && (
                          <Link to={`/article/${m.article_slug}`} data-testid={`lounge-shared-story-${m.id}`}
                            className="group mt-2 flex items-center gap-3 max-w-md border border-white/10 hover:border-kinetic/50 bg-white/[0.02] p-3 transition-colors duration-300">
                            <BookOpen size={15} className="text-kinetic shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-ash">Shared story</p>
                              <p className="text-sm font-medium text-porcelain truncate group-hover:text-kinetic transition-colors">{m.article_title || "Read the story"}</p>
                            </div>
                            <ArrowUpRight size={13} className="text-ash group-hover:text-kinetic shrink-0 ml-auto transition-colors" />
                          </Link>
                        )}
                        <button
                          data-testid={`lounge-flame-button-${m.id}`}
                          onClick={() => toggleFlame(m)}
                          title={user ? (m.flamed ? "Take back your flame" : "Give a flame") : "Sign in to flame"}
                          className={`mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] transition-colors duration-300 ${m.flamed ? "text-kinetic" : "text-ash/70 hover:text-kinetic"}`}>
                          <Flame size={13} fill={m.flamed ? "currentColor" : "none"} />
                          {(m.flames || 0) > 0 ? m.flames : "Flame"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div ref={bottomRef} />
        </div>
      </Reveal>

      <div className="sticky bottom-4 mt-6">
        {user ? (
          <div>
            {image && (
              <div data-testid="lounge-image-preview" className="mb-2 inline-flex items-center gap-2 bg-obsidian/90 backdrop-blur-xl border border-white/15 rounded-2xl p-2">
                <img src={image.url} alt="Attachment preview" className="w-12 h-12 rounded-xl object-cover" />
                <button data-testid="lounge-image-remove-button" onClick={() => setImage(null)} title="Remove photo"
                  className="p-1.5 text-ash hover:text-crimson transition-colors duration-300">
                  <X size={14} />
                </button>
              </div>
            )}
            {topicPickerOpen && (
              <div data-testid="lounge-topic-picker" className="mb-2 flex gap-2 overflow-x-auto no-scrollbar bg-obsidian/90 backdrop-blur-xl border border-white/15 rounded-2xl p-2.5">
                {CATEGORIES.map((c) => (
                  <button key={c.slug} data-testid={`lounge-topic-pick-${c.slug}`}
                    onClick={() => { setDraftTopic(draftTopic === c.slug ? "" : c.slug); setTopicPickerOpen(false); }}
                    className={topicPillCls(draftTopic === c.slug)}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 bg-obsidian/90 backdrop-blur-xl border border-white/15 rounded-full pl-2 pr-2 py-2 focus-within:border-kinetic/60 transition-colors duration-300">
              <input ref={fileInput} data-testid="lounge-image-file-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden" onChange={pickImage} />
              <button
                data-testid="lounge-attach-button"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                title="Attach a photo"
                className="shrink-0 w-10 h-10 rounded-full text-ash hover:text-kinetic flex items-center justify-center transition-colors duration-300 disabled:opacity-40">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              </button>
              <button
                data-testid="lounge-topic-button"
                onClick={() => setTopicPickerOpen((o) => !o)}
                title="Tag a topic"
                className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${draftTopic ? "text-kinetic" : "text-ash hover:text-kinetic"}`}>
                <Hash size={16} />
              </button>
              {draftTopic && (
                <button data-testid="lounge-draft-topic-chip" onClick={() => setDraftTopic("")} title="Remove topic"
                  className="shrink-0 hidden sm:flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.15em] text-kinetic border border-kinetic/40 rounded-full px-2.5 py-1">
                  #{topicLabel(draftTopic)} <X size={10} />
                </button>
              )}
              <input
                data-testid="lounge-composer-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                maxLength={500}
                placeholder={`Speak, ${user.name.split(" ")[0]}… or drop a photo`}
                className="flex-1 min-w-0 bg-transparent text-sm text-porcelain placeholder:text-ash/60 outline-none"
              />
              <span className="text-[10px] font-mono text-ash/50 shrink-0 hidden sm:block">{draft.length}/500</span>
              <button
                data-testid="lounge-send-button"
                onClick={send}
                disabled={(!draft.trim() && !image) || sending || uploading}
                className="shrink-0 w-10 h-10 rounded-full bg-kinetic text-obsidian flex items-center justify-center hover:bg-porcelain disabled:opacity-40 disabled:hover:bg-kinetic transition-colors duration-300">
                <Send size={15} />
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="lounge-signin-cta" className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-obsidian/90 backdrop-blur-xl border border-dashed border-kinetic/40 rounded-3xl sm:rounded-full px-6 py-4">
            <p className="text-sm text-ash">You're reading as a guest — the floor opens once you're in.</p>
            <button
              data-testid="lounge-signin-button"
              onClick={() => openAuth("login")}
              className="shrink-0 bg-kinetic text-obsidian text-[11px] font-mono uppercase tracking-[0.15em] font-medium px-6 py-3 rounded-full hover:bg-porcelain transition-colors duration-300">
              Sign in to speak
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
